import React, { useState, useRef, createElement } from 'react';
import { UploadIcon, XIcon, FileIcon, CheckIcon, AlertCircleIcon } from 'lucide-react';
import { uploadFile, generateStoragePath } from '../firebase/storage';
import Compressor from 'compressorjs';
interface FileUploadProps {
  userId: string;
  fileType: 'thumbnail' | 'product' | 'preview';
  onUploadComplete: (downloadUrl: string) => void;
  onUploadError?: (error: Error) => void;
  onUploadStart?: () => void;
  accept?: string;
  maxSizeMB?: number;
  className?: string;
  buttonText?: string;
  showPreview?: boolean;
  previewUrl?: string;
  isUploading?: boolean;
  maxWidth?: number;
  maxHeight?: number;
}
export const FileUpload: React.FC<FileUploadProps> = ({
  userId,
  fileType,
  onUploadComplete,
  onUploadError,
  onUploadStart,
  accept = '*/*',
  maxSizeMB = 50,
  className = '',
  buttonText = 'Upload File',
  showPreview = true,
  previewUrl,
  isUploading: externalIsUploading,
  maxWidth = 1200,
  maxHeight = 800
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(previewUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTaskRef = useRef<{
    cancel: () => void;
  } | null>(null);
  // Use external uploading state if provided
  const uploading = externalIsUploading !== undefined ? externalIsUploading : isUploading;
  // Check if browser supports WebP
  const isWebPSupported = () => {
    const canvas = document.createElement('canvas');
    if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
      return true;
    }
    return false;
  };
  // Process image (resize and compress)
  const processImage = async (imageFile: File): Promise<File> => {
    return new Promise(resolve => {
      // If it's not an image, return as is
      if (!imageFile.type.startsWith('image/')) {
        resolve(imageFile);
        return;
      }
      // Basic image processing using canvas
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = height * maxWidth / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = width * maxHeight / height;
          height = maxHeight;
        }
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        // Convert to blob
        canvas.toBlob(blob => {
          if (blob) {
            // Create new file from blob
            const resizedFile = new File([blob], imageFile.name, {
              type: imageFile.type
            });
            resolve(resizedFile);
          } else {
            resolve(imageFile); // Fallback to original if conversion fails
          }
        }, imageFile.type, 0.8);
      };
      img.onerror = () => {
        resolve(imageFile); // Fallback to original if loading fails
      };
      // Load image
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) {
          img.src = e.target.result as string;
        }
      };
      reader.onerror = () => resolve(imageFile);
      reader.readAsDataURL(imageFile);
    });
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      // Check file size
      if (selectedFile.size > maxSizeMB * 1024 * 1024) {
        setError(`File size exceeds ${maxSizeMB}MB limit`);
        return;
      }
      setFile(selectedFile);
      setError(null);
      // Create preview for images
      if (selectedFile.type.startsWith('image/') && showPreview) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
      // Auto-upload the file
      handleUpload(selectedFile);
    }
  };
  const handleUpload = async (selectedFile: File = file!) => {
    if (!selectedFile || !userId) return;
    try {
      setIsUploading(true);
      setUploadProgress(0);
      if (onUploadStart) onUploadStart();
      // Process image if it's an image file
      let fileToUpload = selectedFile;
      if (selectedFile.type.startsWith('image/')) {
        try {
          fileToUpload = await processImage(selectedFile);
        } catch (err) {
          // Image processing failed, using original file
        }
      }
      // Generate path with appropriate extension
      const path = generateStoragePath(userId, fileType, fileToUpload.name);
      // Upload to Firebase
      uploadTaskRef.current = uploadFile(fileToUpload, path, progress => {
        setUploadProgress(progress);
      }, error => {
        setError(error.message);
        setIsUploading(false);
        if (onUploadError) onUploadError(error);
      }, downloadUrl => {
        setIsUploading(false);
        setUploadProgress(100);
        onUploadComplete(downloadUrl);
      });
    } catch (err) {
      setError('Upload failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setIsUploading(false);
      if (onUploadError) onUploadError(err as Error);
    }
  };
  const cancelUpload = () => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel();
      uploadTaskRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);
  };
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const resetUpload = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const isImage = file?.type.startsWith('image/') || preview && showPreview;
  const fileName = file?.name || (preview ? 'Uploaded image' : '');
  // Format file size for display
  const formatFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  };
  return <div className={`w-full ${className}`} data-id="element-981">
      <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept={accept} data-id="element-982" />
      {error && <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-md flex items-center" data-id="element-983">
          <AlertCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" data-id="element-984" />
          {error}
        </div>}
      {!file && !preview ? <div onClick={triggerFileInput} className="border-2 border-dashed border-gray-300 dark:border-[#1A3330] rounded-lg p-6 text-center cursor-pointer hover:border-[#2DB489] dark:hover:border-[#4ED1A0] transition-colors" data-id="element-985">
          <UploadIcon className="h-8 w-8 mx-auto mb-2 text-gray-400 dark:text-[#7A8F8A]" data-id="element-986" />
          <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-987">
            {buttonText}
          </p>
          <p className="text-xs text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-988">
            Max size: {maxSizeMB}MB
          </p>
          {fileType === 'thumbnail' || fileType === 'preview' ? <p className="text-xs text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-989">
              Images will be optimized and compressed
            </p> : null}
        </div> : <div className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-990">
          {/* File preview */}
          {isImage && preview ? <div className="relative aspect-video bg-gray-100 dark:bg-[#1A3330]" data-id="element-991">
              <img src={preview} alt="Preview" className="w-full h-full object-contain" data-id="element-992" />
              {!uploading && <button onClick={resetUpload} className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70 transition-colors" data-id="element-993">
                  <XIcon className="h-4 w-4" data-id="element-994" />
                </button>}
            </div> : <div className="p-4 flex items-center" data-id="element-995">
              <FileIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A] mr-3" data-id="element-996" />
              <div className="flex-1 min-w-0" data-id="element-997">
                <p className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] truncate" data-id="element-998">
                  {fileName}
                </p>
                {file && <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-999">
                    {formatFileSize(file.size)}
                  </p>}
              </div>
              {!uploading && <button onClick={resetUpload} className="ml-2 text-gray-400 dark:text-[#7A8F8A] hover:text-gray-600 dark:hover:text-[#B0C1BD]" data-id="element-1000">
                  <XIcon className="h-5 w-5" data-id="element-1001" />
                </button>}
            </div>}
          {/* Progress bar */}
          {uploading && <div className="p-3 bg-gray-50 dark:bg-[#0F1F1D] border-t border-gray-200 dark:border-[#1A3330]" data-id="element-1002">
              <div className="flex items-center justify-between mb-1" data-id="element-1003">
                <span className="text-xs font-medium text-gray-600 dark:text-[#B0C1BD]" data-id="element-1004">
                  {uploadProgress < 100 ? 'Uploading...' : 'Processing...'}
                </span>
                <span className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-1005">
                  {uploadProgress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-[#1A3330] rounded-full h-2 overflow-hidden" data-id="element-1006">
                <div className="bg-[#2DB489] dark:bg-[#4ED1A0] h-2 rounded-full transition-all duration-300 ease-out" style={{
            width: `${uploadProgress}%`
          }} data-id="element-1007"></div>
              </div>
              <div className="mt-2 flex justify-end" data-id="element-1008">
                <button onClick={cancelUpload} className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300" data-id="element-1009">
                  Cancel
                </button>
              </div>
            </div>}
          {/* Success indicator */}
          {!uploading && uploadProgress === 100 && <div className="p-3 bg-green-50 dark:bg-green-900/20 border-t border-green-100 dark:border-green-900/30 flex items-center" data-id="element-1010">
              <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" data-id="element-1011" />
              <span className="text-sm text-green-700 dark:text-green-400" data-id="element-1012">
                Upload complete
              </span>
            </div>}
        </div>}
    </div>;
};